import React from "react";
import { Link } from "react-router-dom";
import { Youtube, Linkedin, Mail, Facebook } from "lucide-react";
import { siteInfo } from "../mock";
import { openCookieConsent } from "./CookieConsent";

const footerGroups = [
  {
    title: "Preparation",
    links: [
      { label: "How to Become a Driver", to: "/how-to-become-a-train-driver" },
      { label: "Start Your Journey", to: "/start-your-journey" },
      {
        label: "All Products",
        to: "/products",
        testId: "footer-products-link",
      },
      { label: "Careers", to: "/careers" },
      { label: "Online Assessment", to: "/online-assessment" },
      { label: "CV Review", to: "/cv" },
      { label: "Interview Preparation", to: "/interview" },
      { label: "Psychometric Tests", to: "/psychometrics" },
      { label: "Complete Package", to: "/complete-career-package" },
    ],
  },
  {
    title: "Training, Courses & Campaigns",
    links: [
      { label: "CPD Courses", to: "/cpd" },
      {
        label: "Free Course: Developing Operational Maturity",
        to: "/cpd/developing-operational-maturity",
      },
      {
        label: "Become a Train Driver at 18",
        to: "/become-a-train-driver-at-18",
      },
      { label: "Service Leavers", to: "/service-leavers" },

      // Armed Forces Week 2026 link temporarily hidden — page template
      // preserved at /armed-forces-week-2027 for the next campaign cycle.
      // { label: "Armed Forces Week 2026", to: "/armed-forces-week" },
    ],
  },
  {
    title: "Career Resources",
    links: [
      { label: "TOCs", to: "/tocs" },
      { label: "Outcomes", to: "/outcomes" },
      {
        label: "What Does a Train Driver Do?",
        to: "/what-does-a-train-driver-do",
      },
      {
        label: "My Train Driver Journey",
        to: "/my-train-driver-journey",
      },
      {
        label: "Blog",
        to: "/blog",
        testId: "footer-blog-link",
      },
      { label: "Contact", to: "/contact" },
    ],
  },
  {
    title: "UKTDA Group",
    links: [
      {
        label: "UK Train Driver Academy",
        to: "https://traindriveracademy.com",
        external: true,
      },
      {
        label: "Train Driver Psychometrics UK",
        to: "https://traindriverpsychometrics.co.uk",
        external: true,
      },
      {
        label: "Railway Careers UK",
        to: "https://railwaycareers.co.uk",
        external: true,
      },
    ],
  },
];

const partnerLinks = [
  /*
    Add genuine partner sites here.

    Example:
    {
      label: "Partner Name",
      to: "https://partnerwebsite.co.uk",
      external: true,
    },
  */
];

function FooterLink({ link }) {
  const className =
    "text-slate-300 hover:text-[#E63B2E] transition-colors";

  if (link.external) {
    return (
      <a
        href={link.to}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        data-testid={link.testId}
      >
        {link.label}
      </a>
    );
  }

  return (
    <Link
      to={link.to}
      data-testid={link.testId}
      className={className}
    >
      {link.label}
    </Link>
  );
}

export default function Footer() {
  const hasPartnerLinks = partnerLinks.length > 0;

  return (
    <footer className="bg-[#07111F] text-slate-200 mt-20 border-t border-white/10">
      {/* Red line */}
      <div className="h-[2px] w-full bg-[#E63B2E]" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-10">
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-10">
          {/* Brand */}
          <div className="xl:col-span-3">
            <Link to="/" className="flex items-center gap-3 mb-5">
              <img
                src="/logo-96.webp"
                alt="Train Driver Foundation"
                width="48"
                height="48"
                loading="lazy"
                className="w-12 h-12 rounded-full object-cover"
              />

              <div>
                <p className="font-extrabold text-white leading-none">
                  TDF
                </p>

                <p className="text-[11px] uppercase tracking-widest text-slate-400">
                  Train Driver Foundation
                </p>
              </div>
            </Link>

            <p className="text-sm text-slate-400 leading-relaxed mb-5">
              The UK rail support specialist for CV reviews, interview
              preparation, psychometric practice and CPD-certified train driver
              courses.
            </p>

            {/* Social icons */}
            <div className="flex gap-3">
              <a
                href={siteInfo.socials.youtube}
                target="_blank"
                rel="noreferrer"
                aria-label="YouTube"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#E63B2E] flex items-center justify-center transition-colors"
              >
                <Youtube className="w-4 h-4" />
              </a>

              <a
                href={siteInfo.socials.linkedin}
                target="_blank"
                rel="noreferrer"
                aria-label="LinkedIn"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#E63B2E] flex items-center justify-center transition-colors"
              >
                <Linkedin className="w-4 h-4" />
              </a>

              <a
                href={siteInfo.socials.email}
                aria-label="Email"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#E63B2E] flex items-center justify-center transition-colors"
              >
                <Mail className="w-4 h-4" />
              </a>

              <a
                href={siteInfo.socials.facebook}
                target="_blank"
                rel="noreferrer"
                aria-label="Facebook"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#E63B2E] flex items-center justify-center transition-colors"
              >
                <Facebook className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Footer link groups */}
          <div className="xl:col-span-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {footerGroups.map((group) => (
                <div key={group.title}>
                  <h4 className="text-white font-bold mb-4 uppercase text-sm tracking-wider">
                    {group.title}
                  </h4>

                  <ul className="space-y-2 text-sm">
                    {group.links.map((link) => (
                      <li key={`${group.title}-${link.to}`}>
                        <FooterLink link={link} />
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {hasPartnerLinks && (
              <div className="mt-10 pt-8 border-t border-white/10">
                <h4 className="text-white font-bold mb-4 uppercase text-sm tracking-wider">
                  Partner Sites
                </h4>

                <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2 text-sm">
                  {partnerLinks.map((link) => (
                    <li key={`partner-${link.to}`}>
                      <FooterLink link={link} />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Newsletter moved to the prominent above-footer band.
              See components/PreFooterNewsletter.jsx
              (mounted in Layout.jsx). */}

          {/* Accreditation logos */}
          <div
            className="xl:col-span-1"
            data-testid="footer-accreditation"
          >
            <h4 className="text-white font-bold mb-4 uppercase text-sm tracking-wider">
              Recognition
            </h4>

            <div className="grid grid-cols-2 xl:grid-cols-1 gap-4 max-w-[260px]">
              <div className="flex flex-col items-center gap-2">
                <div className="bg-white p-1 flex items-center justify-center rounded w-full h-[78px] min-w-[104px]">
                  <img
                    src="/CPD-Member-logo.jpg"
                    alt="CPD Certification Service Member"
                    className="max-h-[68px] object-contain"
                    data-testid="footer-cpd-logo"
                  />
                </div>

                <p className="text-[11px] text-slate-300 text-center leading-tight">
                  Membership No.
                  <br />
                  <span className="font-bold text-white">
                    {siteInfo.cpdMembership}
                  </span>
                </p>
              </div>

              <div className="flex flex-col items-center gap-2">
                <div
                  className="bg-white p-2 flex flex-col items-center justify-center rounded w-full h-[78px] min-w-[104px] text-[#1d70b8]"
                  data-testid="footer-ukrlp-badge"
                  aria-label="UK Register of Learning Providers"
                >
                  <span className="font-extrabold text-[20px] tracking-[0.04em] leading-none">
                    UKRLP
                  </span>

                  <span className="text-[7.5px] uppercase tracking-[0.08em] mt-1 text-center leading-tight font-bold">
                    UK Register of
                    <br />
                    Learning Providers
                  </span>
                </div>

                <p className="text-[11px] text-slate-300 text-center leading-tight">
                  UKPRN
                  <br />
                  <span className="font-bold text-white">
                    {siteInfo.ukprn}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom strip */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 relative">
          
          {/* Company details + Web Wizard */}
          <div className="flex flex-col lg:flex-row items-center justify-center gap-4 lg:gap-6">
            <p className="text-xs text-slate-400 text-center">
              © {siteInfo.year} {siteInfo.companyFull} | Company No.{" "}
              {siteInfo.companyNo} | {siteInfo.tradingName}
            </p>

            <span className="hidden lg:block text-slate-700">
              |
            </span>

            {/* Web Design Wizard credit */}
            <a
              href="https://thewebdesignwizard.co.uk/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Website by The Web Design Wizard"
              className="flex items-center gap-2 group"
            >
              <img
                src="https://blackrodnow.com/webwizard.png"
                alt="The Web Design Wizard"
                className="h-10 w-auto object-contain transition-transform group-hover:scale-105"
                loading="lazy"
              />

              <div className="text-left leading-tight">
                <div className="text-[10px] text-slate-500">
                  Website by
                </div>

                <div className="text-xs font-semibold text-slate-300 group-hover:text-white transition-colors">
                  The Web Design Wizard
                </div>

                <div className="text-[10px] text-[#E63B2E] group-hover:underline">
                  thewebdesignwizard.co.uk
                </div>
              </div>
            </a>
          </div>

          {/* Privacy / cookies */}
          <p className="text-xs text-slate-500 text-center mt-4">
            <Link
              to="/privacy-policy"
              data-testid="footer-privacy-link"
              className="hover:text-[#E63B2E] transition-colors"
            >
              Privacy Policy
            </Link>

            <span className="mx-2 text-slate-700">
              ·
            </span>

            <button
              type="button"
              onClick={openCookieConsent}
              data-testid="footer-cookie-link"
              className="hover:text-[#E63B2E] transition-colors"
            >
              Cookie settings
            </button>
          </p>

          {/* Staff / partner logins */}
          <div className="mt-4 lg:mt-0 lg:absolute lg:right-6 xl:right-8 lg:bottom-6 flex items-center justify-center gap-3">
            <Link
              to="/affiliate/login"
              data-testid="footer-affiliate-link"
              className="text-[10px] text-slate-600 hover:text-slate-400 transition-colors"
            >
              Partners
            </Link>

            <span className="text-slate-800 text-[10px]">
              ·
            </span>

            <Link
              to="/dtm/login"
              data-testid="footer-dtm-link"
              className="text-[10px] text-slate-600 hover:text-slate-400 transition-colors"
            >
              Interview Coach
            </Link>

            <span className="text-slate-800 text-[10px]">
              ·
            </span>

            <Link
              to="/admin/login"
              data-testid="footer-admin-link"
              className="text-[10px] text-slate-600 hover:text-slate-400 transition-colors"
            >
              Admin
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}